import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, ArrowUp, Check, CircleAlert, HelpCircle, Info, Loader2, Wrench } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { MarkdownBody } from "@ncp/ui";
import type { AgentMessage, AgentState, Finding, MemoryItem, Mission, MissionState, Question, Thought, ToolCall } from "../mission/engine";
import { toolLabel } from "../mission/toolNames";
import { useAsk } from "./ask";
import { GrowingInput, TONE_COLOR, Typewriter, useT } from "./kit";
import { MemoryNote } from "./Memory";

type Entry =
  | { kind: "thought"; at: number; id: string; thought: Thought }
  | { kind: "tool"; at: number; id: string; call: ToolCall }
  | { kind: "finding"; at: number; id: string; finding: Finding }
  | { kind: "question"; at: number; id: string; question: Question }
  | { kind: "answer"; at: number; id: string; question: Question }
  | { kind: "message"; at: number; id: string; message: AgentMessage }
  | { kind: "memory"; at: number; id: string; memory: MemoryItem };

/** Everything one agent said and did (what it recalled and remembered included), and every exchange the officer had with it, in order. */
export function conversationOf(agent: AgentState, state: MissionState): Entry[] {
  const questions = Object.values(state.questions).filter((q) => q.agentId === agent.id);
  return [
    ...agent.thoughts.map((t): Entry => ({ kind: "thought", at: t.at, id: t.id, thought: t })),
    ...agent.tools.map((c): Entry => ({ kind: "tool", at: c.at, id: c.id, call: c })),
    ...agent.findings.map((f): Entry => ({ kind: "finding", at: f.at, id: f.id, finding: f })),
    ...questions.map((q): Entry => ({ kind: "question", at: q.at, id: q.id, question: q })),
    ...questions.filter((q) => q.answer && q.answeredAt).map((q): Entry => ({ kind: "answer", at: q.answeredAt!, id: `${q.id}-a`, question: q })),
    ...agent.messages.map((m): Entry => ({ kind: "message", at: m.at, id: m.id, message: m })),
    ...state.memory.filter((m) => m.agentId === agent.id).map((m): Entry => ({ kind: "memory", at: m.at, id: m.id, memory: m })),
  ].sort((a, b) => a.at - b.at);
}

const FINDING_ICON = { ok: Check, info: Info, warn: AlertTriangle, risk: CircleAlert };

/**
 * The running conversation inside an agent's box: its thoughts as messages, the tools it calls, what it finds, the questions it puts to the
 * officer (answerable right here) and the officer's own questions to it with their answers. Follows the newest entry unless the officer
 * has scrolled up to read.
 */
export function Conversation({ agent, state, mission, size, className }: { agent: AgentState; state: MissionState; mission: Mission; size: "card" | "full"; className?: string }) {
  const entries = useMemo(() => conversationOf(agent, state), [agent, state]);
  const shown = size === "card" ? entries.slice(-40) : entries;
  const scroller = useRef<HTMLDivElement>(null);
  const pinned = useRef(true);
  const tail = shown[shown.length - 1];
  const tailSize = tail?.kind === "message" ? tail.message.text.length : 0;
  useEffect(() => {
    const el = scroller.current;
    if (el && pinned.current) el.scrollTo({ top: el.scrollHeight, behavior: size === "card" ? "auto" : "smooth" });
  }, [shown.length, tailSize, size]);
  const newestThought = [...shown].reverse().find((e) => e.kind === "thought")?.id;
  return (
    <div
      ref={scroller}
      onScroll={(e) => { const el = e.currentTarget; pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40; }}
      onClick={(e) => e.stopPropagation()}
      className={`min-h-0 overflow-y-auto overscroll-contain ${className ?? ""}`}
    >
      <div className={`agx-chat ${size === "full" ? "gap-2.5 p-1" : ""}`}>
        {shown.map((e) => <EntryView key={e.id} entry={e} mission={mission} size={size} newest={e.id === newestThought} />)}
        {!shown.length ? <p className="agx-shimmer px-1 text-[11.5px]">…</p> : null}
      </div>
    </div>
  );
}

function EntryView({ entry, mission, size, newest }: { entry: Entry; mission: Mission; size: "card" | "full"; newest: boolean }) {
  const t = useT();
  const { t: tr } = useTranslation();
  const big = size === "full" ? "text-[13px] leading-[1.55] px-3 py-2" : "";
  switch (entry.kind) {
    case "thought": {
      const th = entry.thought;
      return (
        <div className={`agx-msg ${big}`} data-from="agent">
          {newest ? <Typewriter key={th.id} text={t(th.text)} ms={th.ms} active={Date.now() - th.at < th.ms + 400} /> : t(th.text)}
        </div>
      );
    }
    case "tool": {
      const c = entry.call;
      return (
        // What the tool does in plain words, and what it returned: never the internal tool name or a timing.
        <div className={`agx-tool ${size === "full" ? "text-[11.5px]" : ""}`}>
          {c.status === "running" ? <Loader2 className="size-3 shrink-0 agx-spin" style={{ color: "var(--sq)" }} /> : <span className="shrink-0" style={{ color: c.status === "error" ? "var(--agx-rose)" : "var(--sq)" }}>⟐</span>}
          <span className="shrink-0" style={{ color: "var(--sq)" }}>{t(toolLabel(c.name))}</span>
          {c.summary ? <span className="ms-auto min-w-0 truncate ps-2 text-[color:var(--agx-dim)]">· {t(c.summary)}</span> : null}
        </div>
      );
    }
    case "finding": {
      const f = entry.finding;
      const Icon = FINDING_ICON[f.tone];
      return (
        <div className={`agx-msg ${big}`} data-from="finding" style={{ ["--tone" as string]: TONE_COLOR[f.tone] }}>
          <span className="flex gap-1.5">
            <Icon className="mt-[2px] size-3 shrink-0" style={{ color: TONE_COLOR[f.tone] }} />
            <span className="min-w-0">
              {t(f.text)}
              {f.source ? <span className="block text-[11px] agx-faint">↳ {t(f.source)}</span> : null}
            </span>
          </span>
        </div>
      );
    }
    case "question": {
      const q = entry.question;
      return (
        <div className={`agx-msg ${big}`} data-from="question">
          <span className="flex gap-1.5">
            <HelpCircle className="mt-[2px] size-3 shrink-0 text-[color:var(--agx-amber)]" />
            <span className="min-w-0 font-medium">{t(q.text)}</span>
          </span>
          {/* A step that sends letters is answered in the Case Lead's column, where the letters are shown in full (release 1.6). */}
          {!q.answer && !q.letters?.length ? (
            <span className="mt-1.5 flex flex-wrap gap-1">
              {/* An option that needs the officer's own words is answered in the Case Lead's column, where its field is. */}
              {q.options.filter((o) => !o.needsText).map((o) => (
                <button key={o.id} type="button" onClick={(e) => { e.stopPropagation(); mission.answer(q.id, o.id); }} className={`agx-btn h-auto px-2 py-1 ${size === "card" ? "text-[11px]" : "text-[12px]"}`} data-tone={o.tone === "primary" ? "primary" : o.tone === "neutral" ? "ghost" : undefined}>
                  {t(o.label)}
                </button>
              ))}
            </span>
          ) : null}
        </div>
      );
    }
    case "answer": {
      const q = entry.question;
      const chosen = q.options.find((o) => o.id === q.answer);
      return <div className={`agx-msg ${big}`} data-from="officer"><span className="text-[11px] font-semibold uppercase tracking-wider agx-faint">{tr("backoffice:agentic.chat.you")} · </span>{chosen ? t(chosen.label) : q.answer}</div>;
    }
    case "memory":
      return <MemoryNote item={entry.memory} mission={mission} size={size} />;
    case "message": {
      const m = entry.message;
      if (m.from === "officer") return <div className={`agx-msg ${big}`} data-from="officer"><span className="text-[11px] font-semibold uppercase tracking-wider agx-faint">{tr("backoffice:agentic.chat.you")} · </span>{m.text}</div>;
      return (
        <div className={`agx-msg ${big}`} data-from="agent">
          {m.tools?.length ? <span className="mb-1 flex flex-wrap gap-1">{[...new Set(m.tools.map((tool) => t(toolLabel(tool))))].map((label) => <span key={label} className="agx-chip" style={{ ["--c" as string]: "var(--agx-violet)" }}><Wrench className="size-2.5" />{label}</span>)}</span> : null}
          {m.text ? <div className="agx-md [&_p]:my-0.5 [&_ul]:my-0.5"><MarkdownBody streaming={m.streaming}>{m.text}</MarkdownBody></div> : m.streaming ? <span className="agx-shimmer">{tr("backoffice:agentic.lead.thinking")}</span> : null}
          {m.error ? <span className="block text-[color:var(--agx-rose)]">{m.error}</span> : null}
        </div>
      );
    }
  }
}

/** A one-line question to this agent, answered in its box. */
export function AskBox({ agentId, name, size }: { agentId: string; name: string; size: "card" | "full" }) {
  const { t } = useTranslation();
  const { ask, answering } = useAsk();
  const [text, setText] = useState("");
  const busy = answering === agentId;
  const send = () => {
    const value = text.trim();
    if (!value || busy) return;
    setText("");
    ask(agentId, value);
  };
  return (
    <form onSubmit={(e) => { e.preventDefault(); send(); }} onClick={(e) => e.stopPropagation()} className={`flex items-end gap-1.5 rounded-[5px] border border-[color:var(--agx-line)] agx-sunk focus-within:border-[color:var(--agx-gold)]/60 ${size === "card" ? "px-1.5 py-1" : "p-1.5"}`}>
      <GrowingInput value={text} onChange={setText} onSend={send} max={size === "card" ? 84 : 160} placeholder={t("backoffice:agentic.chat.ask", { name })} aria-label={t("backoffice:agentic.chat.ask", { name })}
        className={`min-w-0 flex-1 px-1.5 text-[color:var(--agx-text)] placeholder:text-[color:var(--agx-faint)] ${size === "card" ? "py-[3px] text-[11.5px] leading-[18px]" : "py-2 text-[13px] leading-5"}`} />
      <button type="submit" disabled={!text.trim() || busy} className={`agx-btn shrink-0 p-0 ${size === "card" ? "size-6" : "size-9"}`} data-tone="primary" aria-label={t("backoffice:agentic.lead.send")}>
        {answering === agentId ? <Loader2 className="size-3 agx-spin" /> : <ArrowUp className="size-3" />}
      </button>
    </form>
  );
}
